import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";

import { siteQueryOptions } from "@/api/siteDetails";
import { BroadcastV2ComposeSmsPage } from "@/features/BroadcastV2/components/BroadcastV2ComposeSmsPage";
import { planScheduleWindow } from "@/features/MarketingCalendar/planEvent/draft";
import { planDraftRegistry } from "@/features/MarketingCalendar/planEvent/planDraftRegistry";

const searchSchema = z.object({
  /** Tile whose text seeds the composer (Marketing Calendar plan flow). */
  pendingFrameId: z.string().optional(),
  /** Set when a Marketing Calendar plan opened this composer; Save and
   * Continue returns to the plan wizard with the new broadcast. */
  returnTo: z.literal("marketing-calendar").optional(),
});

export const Route = createFileRoute(
  "/_auth/dragon-tiles/$siteId/broadcastV2/new/sms",
)({
  component: BroadcastV2ComposeSmsRouteComponent,
  validateSearch: zodValidator(searchSchema),
});

export function BroadcastV2ComposeSmsRouteComponent() {
  const { siteId } = Route.useParams();
  const { pendingFrameId, returnTo } = Route.useSearch();
  const { data: site } = useSuspenseQuery(siteQueryOptions(siteId));

  // A plan-wizard entry pre-selects the campaign's audiences on the new
  // broadcast and constrains scheduling to the campaign's dates; the wizard
  // keeps its in-progress draft in this registry.
  const planDraft =
    returnTo === "marketing-calendar"
      ? planDraftRegistry.get(siteId)?.draft
      : undefined;

  return (
    <BroadcastV2ComposeSmsPage
      timezone={site.timezone}
      sendWindowStart={site.send_window_start}
      sendWindowEnd={site.send_window_end}
      pendingFrameId={pendingFrameId}
      returnTo={returnTo}
      initialAudiences={planDraft?.audiences ?? []}
      scheduleWindow={planDraft ? planScheduleWindow(planDraft) : null}
    />
  );
}
